import React, { useEffect, useRef } from "react";
import { Animated, Easing } from "react-native";
import { View, Image as RNImage, Image, Platform, useWindowDimensions } from "react-native";
import logoPng from "../../../assets/logo.png";
import lightLettersPng from "../../../assets/logo2.png";

const Logo = ({ width = 200, height = 50, isDarkMode = false }) => {
  // Professional blue color scheme
  const colors = {
    light: {
      background: "#f0f4f8", // Light blue-gray background
      primary: "#1a73e8", // Google Blue
      secondary: "#174ea6", // Darker blue
      text: "#202124", // Near black for text
      squares: [
        "#1a73e8", // Google Blue
        "#4285f4", // Lighter blue
        "#5094ed", // Mid blue
        "#699df0", // Soft blue
        "#83a7f3", // Lightest blue
      ],
    },
    dark: {
      background: "#1a1c1e", // Dark background
      primary: "#8ab4f8", // Light blue for dark mode
      secondary: "#aecbfa", // Lighter blue for dark mode
      text: "#e8eaed", // Light gray for text
      squares: [
        "#8ab4f8", // Light blue
        "#93bbf9", // Slightly lighter
        "#9cc2fa", // Even lighter
        "#a5c9fb", // Very light
        "#aecbfa", // Lightest
      ],
    },
  };

  const theme = isDarkMode ? colors.dark : colors.light;

  // Choose asset per theme
  const baseLogo = isDarkMode ? lightLettersPng : logoPng;
  const glowColor = isDarkMode ? "#99d1a7" : "#004d25";

  // Both images are identical in size
  const sizeComp = 1;


  // Scale logo larger on wide screens (keeps mobile reasonable)
  const { width: screenW } = useWindowDimensions();
  const scaleFactor = screenW > 1280 ? 2.0 : screenW > 768 ? 1.8 : 1.35;

  // Animated glow (RN-safe) on top of the PNG
  const GlowImage = Animated.createAnimatedComponent(RNImage);
  const pulse = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 1200,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0,
          duration: 1200,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);

  const glowOpacity = pulse.interpolate({
    inputRange: [0, 1],
    outputRange: [0.25, 0.7],
  });

  return (
    <View style={{ width, height, position: "relative", alignItems: "center", justifyContent: "center", overflow: "visible" }}>
      {/* Pulsing glow layer (behind) */}
      <GlowImage
        source={baseLogo}
        resizeMode="contain"
        style={[
          { position: "absolute", left: 0, top: 0, width: "100%", height: "100%" },
          {
            opacity: glowOpacity,
            transform: [{ scale: scaleFactor * sizeComp * 1.06 }],
            ...(Platform.OS === "web"
              ? { filter: "blur(10px)" }
              : {
                  // iOS/Android shadow-based halo (Android uses elevation minimally)
                  shadowColor: glowColor,
                  shadowOpacity: 0.85,
                  shadowRadius: 18,
                  shadowOffset: { width: 0, height: 0 },
                }),
          },
        ]}
      />

      {/* Crisp foreground image */}
      <RNImage
        source={baseLogo}
        resizeMode="contain"
        style={{ width: "100%", height: "100%", transform: [{ scale: scaleFactor * sizeComp }], zIndex: 1 }}
      />
    </View>
  );
};

export default Logo;
